import { Component, computed, effect, inject, OnDestroy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { FormFileUploadComponent, FormInputComponent, FormNumberComponent, FormWysiwygComponent } from '@shared/forms';
import { MediaFacade } from '@app/store/media';
import { MediaItem } from '@app/store/medias';
import { MediaType, getMediaTypeAccept } from '@app/store/shared/models';
import { MediaFormService } from '@app/all-media/media-form.service';
import { MediaSelectionService, MediaTarget } from '../media-selection.service';

@Component({
    selector: 'app-upload-media',
    templateUrl: './upload-media.component.html',
    styleUrls: ['./upload-media.component.css'],
    imports: [ReactiveFormsModule, TranslateModule, ButtonModule, FormInputComponent, FormFileUploadComponent, FormNumberComponent, FormWysiwygComponent],
    providers: [MediaFormService],
})
export class UploadMediaComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly mediaFacade = inject(MediaFacade);
    private readonly mediaFormService = inject(MediaFormService);
    private readonly mediaSelectionService = inject(MediaSelectionService);
    private readonly translateService = inject(TranslateService);

    readonly mediaType: MediaType;
    readonly target: MediaTarget;

    readonly saving = this.mediaFacade.saving;
    readonly form = this.mediaFormService.form;
    readonly fileAccept = computed(() => getMediaTypeAccept(this.mediaType));

    readonly title = computed(() => {
        const typeLabel = this.translateService.instant(`media.type.${this.mediaType}`);
        const key = this.mediaType === 'text' ? 'dialogs.selectMedia.upload.createTypeTitle' : 'dialogs.selectMedia.upload.typeTitle';
        return this.translateService.instant(key, { type: typeLabel });
    });

    constructor() {
        const parentParams = this.route.parent?.snapshot.params ?? {};
        const ownParams = this.route.snapshot.params;
        const fallbackType = <string>(parentParams['mediaType'] ?? 'video');
        this.mediaType = <MediaType>(ownParams['type'] ?? fallbackType.split(',')[0]);
        this.target = <MediaTarget>(parentParams['mediaTarget'] ?? 'mediaId');

        this.form.controls.type.setValue(this.mediaType);

        effect(() => {
            const createdMedia = this.mediaFacade.createdMedia();
            if (!createdMedia) return;

            this.mediaSelectionService.select(<MediaItem>{ id: createdMedia.id, name: createdMedia.name, type: createdMedia.type }, this.target);
            this.close();
        });

        effect(() => {
            if (this.mediaFacade.saving() === false && this.mediaFacade.saved() === false) {
                this.form.enable();
                this.mediaFormService.onTypeChange(this.mediaType);
            }
        });
    }

    ngOnDestroy(): void {
        this.mediaFacade.clear();
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        if (this.form.invalid) {
            return;
        }

        this.form.disable();
        const value = this.form.getRawValue();

        this.mediaFacade.createMedia({
            name: value.name,
            type: this.mediaType,
            mediaId: value.mediaId || undefined,
            content: value.content ?? undefined,
            duration: value.duration ?? undefined,
        });
    }

    goBack(): void {
        const cameFromTypeChooser = this.route.snapshot.params['type'] != null;
        if (cameFromTypeChooser) {
            this.router.navigate(['../../select-type'], { relativeTo: this.route });
            return;
        }
        this.router.navigate(['../'], { relativeTo: this.route });
    }

    close(): void {
        const outlet = this.route.parent?.outlet ?? 'dialog';
        this.router.navigate([{ outlets: { [outlet]: null } }]);
    }
}
